/**
 * OAuth permission scopes requested by SkyDeck.
 *
 * IMPORTANT: This deliberately does NOT use `transition:generic`. That legacy
 * scope grants read/write access to the user's *entire* atproto repository —
 * including records belonging to completely unrelated applications — which is a
 * significant over-reach for a Bluesky client.
 *
 * Instead we request granular scopes (per the atproto "Auth Scopes" proposal,
 * bluesky-social/proposals#0011) that are scoped strictly to the Bluesky
 * (`app.bsky` / `chat.bsky`) lexicons this app actually uses:
 *
 *   - `repo:<collection>`  → create/update/delete only the listed record types.
 *   - `rpc:<lxm>?aud=<svc>` → call the Bluesky AppView / Chat service via the PDS
 *                             service proxy, bounded to those audiences.
 *   - `blob:<mime>`        → upload images and videos only.
 *
 * Notes on syntax (validated against `@atproto/oauth-scopes`):
 *   - `atproto` is the required base scope.
 *   - `rpc:*?aud=*` is forbidden, but `rpc:*?aud=<specific-did>` is allowed.
 *   - The `#` in a service DID must be percent-encoded as `%23` inside a scope.
 */

export const BSKY_APPVIEW_AUD = 'did:web:api.bsky.app#bsky_appview';
export const BLACKSKY_APPVIEW_AUD = 'did:web:api.blacksky.community#bsky_appview';
export const BSKY_CHAT_AUD = 'did:web:api.bsky.chat#bsky_chat';

/**
 * AppView service audiences that authenticated requests may be proxied to. Each
 * must be pre-granted here because OAuth scopes are fixed at sign-in, so the
 * user can switch between these AppViews later without re-authenticating. Keep
 * this in sync with the built-in presets in `~/api/globals/appview`. Custom
 * AppViews added by the user aren't covered and would require re-login (or an
 * app-password account, which isn't scope-restricted).
 *
 * NOTE: this module is imported at build time to generate the OAuth client
 * metadata, so it must not import the runtime AppView store (which touches
 * `localStorage`). Hence the auds are duplicated as plain constants.
 */
const APPVIEW_AUDS: readonly string[] = [BSKY_APPVIEW_AUD, BLACKSKY_APPVIEW_AUD];

// `#` -> `%23` for use within a scope's query string.
const enc = (aud: string) => aud.replace(/#/g, '%23');

export const OAUTH_SCOPES: readonly string[] = [
	// Required base scope.
	'atproto',

	// Record types this client writes to the user's own repository.
	'repo:app.bsky.actor.profile',
	'repo:app.bsky.feed.post',
	'repo:app.bsky.feed.like',
	'repo:app.bsky.feed.repost',
	'repo:app.bsky.feed.threadgate',
	'repo:app.bsky.feed.postgate',
	'repo:app.bsky.graph.follow',
	'repo:app.bsky.graph.block',
	'repo:app.bsky.graph.list',
	'repo:app.bsky.graph.listitem',
	'repo:app.bsky.graph.listblock',
	'repo:app.bsky.graph.starterpack',

	// Read/write AppView APIs (timelines, profiles, search, notifications,
	// mutes, lists, …) through the PDS service proxy, for every selectable
	// AppView so the user can switch between them without re-authenticating.
	...APPVIEW_AUDS.map((aud) => `rpc:*?aud=${enc(aud)}`),
	// Allow fetching custom feed skeletons from any feed generator service.
	'rpc:app.bsky.feed.getFeedSkeleton?aud=*',
	// Direct messages, via the Bluesky chat service.
	`rpc:*?aud=${enc(BSKY_CHAT_AUD)}`,
	// Submitting moderation reports (target service / labeler varies).
	'rpc:com.atproto.moderation.createReport?aud=*',

	// Media uploads.
	'blob:image/*',
	'blob:video/*',
];

/** Space-delimited scope string used in OAuth requests and client metadata. */
export const OAUTH_SCOPE = OAUTH_SCOPES.join(' ');
